import useBottomSheet from 'hooks/common/useBottomSheet';
import { useCallback, useEffect, useRef, useState } from 'react';

type NickStatus = 'inspecting' | 'denied' | 'confirmed';

const useProfileEdit = () => {
  const { close, open } = useBottomSheet();

  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(() => PhotoManager.getPhotoURL(myinfo, true));
  const [nickStatus, setNickStatus] = useState<NickStatus>();
  const [nick, setNick] = useState<string>(myinfo.nick);
  const uploadButtonRef = useRef<HTMLDivElement>(null);

  const goToPage = (defPage: string) => {
    Transition.showPage(defPage, nickStatus);
  };

  const saveImage = useCallback((image: string) => {
    setProfileImageUrl(image);
    PhotoManager.convertImage(image, 50, 50, (thumb: string) => {
      const photoList = [
        { data: image, fname: 'main.jpg', isPrivate: false },
        { data: thumb, fname: 'thumb.jpg', isPrivate: false },
      ];
      PhotoManager.uploadPhotos(photoList, (result: any[]) => {
        if (result && result[0]?.token && result[1]?.token) {
          PhotoManager.savePhotoURL(result[0].token, result[1].token);
        }
      });
    });
  }, []);

  const cropImage = (image: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const size = Math.min(img.width, img.height);
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(image);
          const sx = (img.width - size) / 2;
          const sy = (img.height - size) / 2;
          ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
          const croppedImage = canvas.toDataURL('image/jpeg', 1.0);
          resolve(croppedImage);
        } catch (error) {
          resolve(image);
        }
      };
      img.onerror = () => resolve(image);
      img.src = image;
    });
  };

  const handleUploadPhoto = () => {
    if (!uploadButtonRef.current) return;
    window.FileChooser.btnClicked?.({ target: uploadButtonRef.current });
  };

  useEffect(() => {
    const nickCheckRef = fbRefMyUserInfo.child('log').child('nickcheck');
    if (!uploadButtonRef.current) return;

    const openUploadPhoto = async (image: string) => {
      if (!image) return;
      const croppedImage = await cropImage(image);
      open('UploadPhoto', {
        id: 'UploadPhoto',
        data: { imgSrc: croppedImage, onSaveImage: () => saveImage(croppedImage) },
      });
    };

    window.FileChooser.setActor(uploadButtonRef.current, openUploadPhoto, null, 0.7, 1000);

    const getNick = (): Promise<string> => {
      return new Promise((resolve) => {
        fbRefMyUserInfo
          .child('myinfo')
          .child('nick')
          .once('value', (snap: any) => {
            resolve(snap.val());
          });
      });
    };

    const onNickChange = async (snap: any) => {
      if (!snap.val()) {
        BLog.log('[MyDataManager:startNickListener] There is no nick Listener data');
        return;
      }
      setNickStatus(snap.val().status);
      setNick(snap.val().nick);

      if (snap.val().status === 'confirmed') {
        const newNick = await getNick();
        if (newNick) {
          myinfo.nick = newNick;
          if (myinfo.prevNick === null || myinfo.prevNick !== newNick) {
            myinfo.prevNick = newNick;
            fbRefMyUserInfo.child('myinfo').child('prevNick').set(newNick);
            Braze.setAttributeNick();
          }
        }
      }
    };

    nickCheckRef.on('value', onNickChange);

    return () => {
      window.FileChooser.release();
      nickCheckRef.off('value', onNickChange);
    };
  }, [open, saveImage]);

  return {
    profileImageUrl,
    nick,
    nickStatus,
    uploadButtonRef,
    handleUploadPhoto,
    goToPage,
    close,
  };
};

export default useProfileEdit;
